/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/api/menuSchedules.ts
 * 菜单排期 API 服务
 * 
 * 功能：
 * - 菜单排期列表查询
 * - 菜单排期创建（支持两种模式：选择菜单 vs 快速创建）
 * - 菜单排期详情查询
 * - 菜单排期更新
 * - 菜单排期删除
 * - 菜单排期发布/取消发布
 * 
 * 版本：M4-Menu-Model-Refactoring（支持 Menu 模型）
 */

import { httpClient } from './http'
import type { PaginatedResponse } from '../types'
// ⚠️ 生成类型仅供参考：本文件自行定义 MenuSchedule/MenuScheduleRequest 以避免 ID number vs string 冲突

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

/**
 * 菜品详情（排期菜单中的菜品）
 * ⭐ M11 扩展：统一企业端与小程序端菜品卡片显示字段
 */
export interface DishDetail {
    id?: string
    name?: string
    price?: string
    /** ⭐ P2-A: 基准价（DishDynamicPrice > Dish.price） */
    base_price?: string
    /** ⭐ P2-A: 供餐价格（经 EMS 餐标/补贴类型修正后的价格） */
    effective_price?: string
    /** ⭐ Gap-1: 排期级自定义价格（优先级最高） */
    custom_price?: string | null
    description?: string
    image?: string
    image_url?: string
    // ⭐ M18-FIX: 兼容历史数据 ON_SALE/OFF_SALE 状态（序列化器已做规范化，前端做防御）
    status?: 'AVAILABLE' | 'UNAVAILABLE' | 'ON_SALE' | 'OFF_SALE'
  // ⭐ M11 新增：辣度、分类、搭配、销量、评分
  spiciness_level?: string            // 辣度代码（NONE/MILD/MEDIUM/HOT）
  spiciness_level_display?: string    // 辣度中文（不辣/微辣/中辣/超辣）
  category_name?: string              // 分类名称
  cuisine_name?: string               // 菜系名称
  dish_type?: string                  // 菜品类型
  composition?: Array<{               // 搭配信息
    type: string
    name: string
    weight_value?: string
    weight_unit?: string
  }> | null
  tags?: string[]                     // 标签列表
  sales_total?: number                // 销量
  rating_score?: number               // 评分
  taste_score?: number                // 口味评分
  /** ⭐ 菜品规格：是否配置了可选规格 */
  has_specs?: boolean
  /** ⭐ 菜品规格定义（name + options[]），由运营端配置 */
  specifications?: Array<{
    name: string
    required?: boolean
    options: string[]
  }> | null
}

/**
 * 菜单详情（嵌套在排期中）
 */
export interface MenuDetail {
  id: string
  name: string
  dishes_detail: DishDetail[]
  tags_detail: Array<{
    id?: string
    name?: string
    color?: string
  }>
  suitable_meal_segments_detail: Array<{
    code?: string
    label?: string
  }>
  /** ⭐ P2-A: 当前 EMS 餐标（Decimal 字符串） */
  meal_budget?: string | null
  /** ⭐ P2-A: 餐补类型 (MEAL_BUDGET/FIXED/RATIO/NONE) */
  subsidy_type?: string | null
}

/**
 * 菜单排期类型（独立定义，不扩展生成类型）
 * 
 * 原因：生成类型中 ID 为 number，前端规范统一使用 string，
 * 且 readonly 字段的 null/undefined 差异会导致大量类型冲突。
 * 
 * @see GeneratedMenuSchedule 生成的类型（仅供参考，不直接使用）
 */
export interface MenuSchedule {
  id: string
  name: string
  menu?: string // 菜单ID（新增）
  menu_detail?: MenuDetail // 菜单详情（新增）
  /** management 列表使用的轻量菜品数量；完整菜品仅由 retrieve 返回。 */
  menu_items_count?: number
  workplace: string
  workplace_name: string
  restaurant: string
  restaurant_name: string
  enterprise: string
  enterprise_name: string
  merchant: string
  merchant_name: string
  dishes: string[]
  meal_segment: string
  meal_segment_name: string
  service_type: string
  consumption_scene: string
  consumption_scene_name: string
  time_rule?: string
  time_rule_name?: string
  order_deadline?: string
  supplement_deadline?: string
  deadline_config?: unknown
  is_orderable?: boolean // ⭐ M5-NEW: 是否可点餐（动态计算）
  status: 'DRAFT' | 'PUBLISHED' | 'UNPUBLISHED'
  status_display?: string // ⭐ M6-NEW: 状态显示名（待发布/待上架/已上架）
  is_enabled?: boolean // ⭐ M6-NEW: 上架开关（运营端控制）
  disabled_dates?: string[] // ⭐ M16-NEW: 按日期下架
  enabled_by?: string // ⭐ M6-NEW: 上架操作人ID
  enabled_by_name?: string // ⭐ M6-NEW: 上架操作人姓名
  enabled_at?: string // ⭐ M6-NEW: 上架操作时间
  /** ⭐ M17-NEW: 单日排期日期（YYYY-MM-DD） */
  schedule_date?: string
  /** ⚠️ 兼容字段：迁移期保持 scheduled_dates=[schedule_date] */
  scheduled_dates: string[]
  created_by: string
  created_by_name: string
  updated_by: string
  updated_by_name: string
  published_by?: string
  published_by_name?: string
  published_at?: string
  created_at: string
  updated_at: string
  // R5: 餐厅/EMS 单餐次供应总量
  supply_capacity?: number | null
  supply_sold?: number
  supply_remaining?: number | null
}

/**
 * 旧的 MenuItem 类型（向后兼容）
 * @deprecated 使用 menu_detail.dishes_detail 替代
 */
export interface MenuItem {
  dish_id: string
  dish_name?: string
  price: number | string
  image_url?: string
}

/**
 * 菜单排期创建请求（支持两种模式）
 */
export interface MenuScheduleRequest {
  // ========================================
  // 模式 1：选择菜单（推荐）
  // ========================================
  /**
   * 菜单ID（选择已有菜单）
   * 
   * 如果提供此字段，则 name 和 dishes 从菜单中自动获取
   */
  menu?: string

  // ========================================
  // 模式 2：快速创建（向后兼容）
  // ========================================
  /**
   * 菜单名称（快速创建模式）
   * 
   * 如果未提供 menu，则必须提供此字段
   * 后端会自动创建一个临时菜单
   */
  name?: string

  /**
   * 菜品ID列表（快速创建模式）
   * 
   * 如果未提供 menu，则必须提供此字段
   */
  dishes?: string[]

  // ========================================
  // 公共字段（两种模式都需要）
  // ========================================
  workplace: string
  restaurant: string
  meal_segment: string
  /**
   * 消费类型（消费场景）常量 code（后端唯一真源字段）
   *
   * 说明：
   * - 后端会基于 service_type 回显 readOnly 字段：consumption_scene / consumption_scene_name
   * - 写入侧不要提交 consumption_scene（避免契约漂移）
   */
  service_type: string
  time_rule?: string
  scheduled_dates: string[]
}

export interface MenuScheduleFilters {
  /** 管理列表轻量档位；详情和编辑回填仍须按 ID retrieve。 */
  response_profile?: 'management' | 'legacy'
  workplace?: string // ⭐ M5-UPDATE: 职场 ID（改名保持一致）
  workplace_id?: string // 向后兼容
  restaurant?: string
  restaurant_id?: string // 向后兼容
  meal_segment?: string
  consumption_scene?: string // ⭐ M5-NEW: 消费场景过滤
  /** ✅ 后端真实过滤字段（推荐写入） */
  service_type?: string
  scheduled_date_single?: string // ⭐ M5-NEW: 单个日期查询（YYYY-MM-DD）
  scheduled_dates?: string // 多个日期查询（逗号分隔）
  /** ⭐ M5: 周范围过滤（也用于月范围/双月范围），后端会优先按 schedule_date 过滤 */
  week_start?: string
  week_end?: string
  workplace_ids?: string
  /** 运营管理列表多选消费类型，逗号分隔。 */
  consumption_scenes?: string
  /** 运营管理列表多选餐段，逗号分隔。 */
  meal_segments?: string
  status?: 'DRAFT' | 'PUBLISHED'
  is_enabled?: boolean // ⭐ M6-NEW: 上架状态过滤
  page?: number
  page_size?: number
  /** DRF OrderingFilter: e.g. '-schedule_date' */
  ordering?: string
}

export interface MenuScheduleManagementCoverageParams {
  week_start: string
  week_end: string
  workplace_ids?: string
  consumption_scenes?: string
  meal_segments?: string
}

export interface MenuScheduleManagementCoverageItem {
  workplace_id: number
  restaurant_id: number
  consumption_type: string
  meal_segment: string
  week_offset: 0 | 1
}

export interface MenuScheduleManagementCoverageResponse {
  week_start: string
  week_end: string
  count: number
  scheduled_configurations: MenuScheduleManagementCoverageItem[]
}

export interface PaginatedMenuScheduleResponse extends PaginatedResponse<MenuSchedule> {
  page: number
  page_size: number
  total_pages: number
}

class MenuScheduleApiService {
  private readonly endpoint = 'merchants/schedules/'

  /**
   * 获取排期列表
   * 
   * @param params 查询参数
   * @returns 排期列表（分页）
   * 
   * @example
   * ```typescript
   * // 查询职场的排期
   * const schedules = await menuScheduleApi.list({
   *   workplace_id: '123',
   *   meal_segment: 'WC0003',
   *   status: 'PUBLISHED'
   * })
   * ```
   */
  async list(params?: MenuScheduleFilters): Promise<PaginatedMenuScheduleResponse> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.get<PaginatedMenuScheduleResponse | { data: PaginatedMenuScheduleResponse }>(
      this.endpoint,
      { params }
    )
    const raw: unknown = resp.data
    const normalized: unknown = (() => {
      if (isRecord(raw) && Array.isArray(raw.results)) return raw
      if (isRecord(raw) && isRecord(raw.data) && Array.isArray(raw.data.results)) return raw.data
      return raw
    })()

    if (!isRecord(normalized) || !Array.isArray(normalized.results)) {
      throw new Error('Invalid menu schedule list response shape')
    }
    const results = normalized.results as MenuSchedule[]

    // 统一补齐分页字段
    const page = params?.page || 1
    const pageSize = params?.page_size || 20
    const count = typeof normalized.count === 'number' ? normalized.count : results.length
    const totalPages = Math.max(1, Math.ceil(count / pageSize))

    return {
      results,
      count,
      page,
      page_size: pageSize,
      total_pages: typeof normalized.total_pages === 'number' ? normalized.total_pages : totalPages,
      next: typeof normalized.next === 'string' ? normalized.next : null,
      previous: typeof normalized.previous === 'string' ? normalized.previous : null
    }
  }

  /**
   * 运营/商户管理列表专用轻量读取。
   * 显式封装 profile，避免把共享 list 的默认契约改成轻量后影响员工端或历史调用方。
   */
  async listManagement(params?: Omit<MenuScheduleFilters, 'response_profile'>): Promise<PaginatedMenuScheduleResponse> {
    return this.list({ ...params, response_profile: 'management' })
  }

  /** 运营统计卡片专用双周覆盖数据，不返回菜单或菜品详情。 */
  async getManagementCoverage(
    params: MenuScheduleManagementCoverageParams
  ): Promise<MenuScheduleManagementCoverageResponse> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.get<
      MenuScheduleManagementCoverageResponse | { data: MenuScheduleManagementCoverageResponse }
    >(`${this.endpoint}management_coverage/`, { params })
    const raw: unknown = resp.data
    const normalized = isRecord(raw) && isRecord(raw.data) ? raw.data : raw
    if (!isRecord(normalized) || !Array.isArray(normalized.scheduled_configurations)) {
      throw new Error('Invalid menu schedule management coverage response shape')
    }
    return normalized as unknown as MenuScheduleManagementCoverageResponse
  }

  /**
   * 获取排期详情
   * 
   * @param id 排期ID
   * @returns 排期详情（含菜单详情）
   * 
   * @example
   * ```typescript
   * const schedule = await menuScheduleApi.getById('1')
   * console.log(schedule.menu_detail) // 菜单详情（新增）
   * console.log(schedule.menu_detail?.dishes_detail) // 菜品列表
   * console.log(schedule.menu_detail?.tags_detail) // 标签列表
   * ```
   */
  async getById(id: string): Promise<MenuSchedule> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.get<MenuSchedule | { data: MenuSchedule }>(`${this.endpoint}${id}/`)
    const raw: unknown = resp.data
    const item: MenuSchedule = (isRecord(raw) ? ((toRecord(raw).data ?? raw) as MenuSchedule) : (raw as MenuSchedule))

    if (!item || !item.id) {
      throw new Error('Invalid menu schedule detail response shape')
    }

    return item
  }

  /**
   * 创建排期（支持两种模式）
   * 
   * @param data 排期数据
   * @returns 创建的排期
   * 
   * @example
   * ```typescript
   * // 模式 1：选择菜单（推荐）
   * const schedule = await menuScheduleApi.create({
   *   menu: '123', // 菜单ID
   *   workplace: '456',
   *   restaurant: '789',
   *   meal_segment: 'WC0003',
   *   service_type: 'ALWS01',
   *   scheduled_dates: ['2025-11-25', '2025-11-26']
   * })
   * 
   * // 模式 2：快速创建（向后兼容）
   * const schedule = await menuScheduleApi.create({
   *   name: '临时菜单',
   *   dishes: ['1', '2', '3'],
   *   workplace: '456',
   *   restaurant: '789',
   *   meal_segment: 'WC0003',
   *   service_type: 'ALWS01',
   *   scheduled_dates: ['2025-11-25', '2025-11-26']
   * })
   * ```
   * 
   * @note
   * - 模式 1（推荐）：选择已有菜单，快速排期，菜单可复用
   * - 模式 2（兼容）：直接选择菜品，后端自动创建临时菜单
   * - 两种模式互不冲突，根据业务场景选择
   */
  async create(data: MenuScheduleRequest): Promise<MenuSchedule> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.post<MenuSchedule | { data: MenuSchedule }>(this.endpoint, data)
    const raw: unknown = resp.data
    const item: MenuSchedule = (isRecord(raw) ? ((toRecord(raw).data ?? raw) as MenuSchedule) : (raw as MenuSchedule))

    if (!item || !item.id) {
      throw new Error('Invalid menu schedule create response shape')
    }

    return item
  }

  /**
   * 更新排期
   */
  async update(id: string, data: Partial<MenuScheduleRequest>): Promise<MenuSchedule> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.patch<MenuSchedule | { data: MenuSchedule }>(`${this.endpoint}${id}/`, data)
    const raw: unknown = resp.data
    const item: MenuSchedule = (isRecord(raw) ? ((toRecord(raw).data ?? raw) as MenuSchedule) : (raw as MenuSchedule))

    if (!item || !item.id) {
      throw new Error('Invalid menu schedule update response shape')
    }

    return item
  }

  /**
   * 删除排期
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.endpoint}${id}/`)
  }

  /**
   * 发布排期
   */
  async publish(id: string): Promise<MenuSchedule> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.post<MenuSchedule | { data: MenuSchedule }>(`${this.endpoint}${id}/publish/`)
    const raw: unknown = resp.data
    return (isRecord(raw) ? ((toRecord(raw).data ?? raw) as MenuSchedule) : (raw as MenuSchedule))
  }

  /**
   * 取消发布排期
   */
  async unpublish(id: string): Promise<MenuSchedule> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.post<MenuSchedule | { data: MenuSchedule }>(`${this.endpoint}${id}/unpublish/`)
    const raw: unknown = resp.data
    return (isRecord(raw) ? ((toRecord(raw).data ?? raw) as MenuSchedule) : (raw as MenuSchedule))
  }

  /**
   * ⭐ M6-NEW: 上架排期（运营端独占）
   * 
   * 将已发布的排期设置为可点餐状态
   * 前提：排期必须是 PUBLISHED 状态
   */
  async enable(id: string): Promise<{ message: string; id: string; is_enabled: boolean; status_display: string }> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.post(`${this.endpoint}${id}/enable/`)
    const raw: unknown = resp.data
    return (isRecord(raw) ? ((toRecord(raw).data ?? raw) as { message: string; id: string; is_enabled: boolean; status_display: string }) : (raw as { message: string; id: string; is_enabled: boolean; status_display: string }))
  }

  /**
   * ⭐ M6-NEW: 下架排期（运营端独占）
   * 
   * 将已上架的排期设置为不可点餐状态
   * 前提：排期必须是 PUBLISHED 状态
   */
  async disable(id: string): Promise<{ message: string; id: string; is_enabled: boolean; status_display: string }> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.post(`${this.endpoint}${id}/disable/`)
    const raw: unknown = resp.data
    return (isRecord(raw) ? ((toRecord(raw).data ?? raw) as { message: string; id: string; is_enabled: boolean; status_display: string }) : (raw as { message: string; id: string; is_enabled: boolean; status_display: string }))
  }

  /**
   * ⭐ M6-NEW: 批量上架排期（运营端独占）
   */
  async batchEnable(ids: string[], dates?: string[]): Promise<{ message: string; updated_count: number }> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.post(`${this.endpoint}batch_enable/`, { schedule_ids: ids, ...(dates?.length ? { dates } : {}) })
    const raw: unknown = resp.data
    return (isRecord(raw) ? ((toRecord(raw).data ?? raw) as { message: string; updated_count: number }) : (raw as { message: string; updated_count: number }))
  }

  /**
   * ⭐ M6-NEW: 批量下架排期（运营端独占）
   */
  async batchDisable(ids: string[], dates?: string[]): Promise<{ message: string; updated_count: number }> {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.post(`${this.endpoint}batch_disable/`, { schedule_ids: ids, ...(dates?.length ? { dates } : {}) })
    const raw: unknown = resp.data
    return (isRecord(raw) ? ((toRecord(raw).data ?? raw) as { message: string; updated_count: number }) : (raw as { message: string; updated_count: number }))
  }

  /**
   * 批量创建排期（智能合并模式）⭐
   * 
   * 输入：schedules - 排期数组
   * 输出：Promise<MenuSchedule[]>
   * 
   * 功能：
   * - 同一职场+餐厅+餐段的排期会自动合并日期
   * - 支持直接发布（status='PUBLISHED'）
   * - 自动推断 operator_tenant/merchant/enterprise
   */
  async batchCreate(schedules: Array<{
    workplace_id: string
    restaurant_id: string
    menu_id: string
    meal_segment: string
    scheduled_dates: string[]
    status?: 'DRAFT' | 'PUBLISHED'
    service_type?: string
  }>): Promise<MenuSchedule[]> {
    // ✅ 后端已统一包装为 {data,message,status}，httpClient 会自动 extractPayload 并返回 data
    const response = await httpClient.post<MenuSchedule[]>(
      `${this.endpoint}batch_create/`,
      { schedules },
    )
    return Array.isArray(response) ? response : []
  }

  /**
   * ⭐ M17-NEW: 批量创建/更新排期（每日一条记录）
   *
   * 输入：与 create/update 近似，但 scheduled_dates 允许多日期
   * 输出：MenuSchedule[]（每日期一条）
   */
  async batchUpsert(data: {
    name?: string
    workplace: string
    restaurant: string
    meal_segment: string
    service_type: string
    menu?: string
    /** ⭐ Gap-1: 支持结构化菜品数据（含 custom_price），兼容纯 ID 列表 */
    dishes?: string[] | Array<{ id: string; custom_price?: string; quantity?: number; sort_order?: number }>
    scheduled_dates: string[]
    status?: 'DRAFT' | 'PUBLISHED'
    /** ⭐ v1.8.1 R4: 菜品供应量映射 {dish_id: max_supply | null} */
    dish_max_supplies?: Record<string, number | null>
  }): Promise<MenuSchedule[]> {
    // ✅ 后端已统一包装为 {data,message,status}，httpClient 会自动 extractPayload 并返回 data
    const response = await httpClient.post<MenuSchedule[]>(
      `${this.endpoint}batch_upsert/`,
      data,
    )
    return Array.isArray(response) ? response : []
  }

  /**
   * 批量发布排期
   * 输入：ids (string[]) - 排期ID数组
   * 输出：Promise<void>
   */
  async batchPublish(ids: string[]): Promise<void> {
    await httpClient.post(
      `${this.endpoint}batch_update_status/`,
      { ids, status: 'PUBLISHED' },
    )
  }

  /**
   * 批量取消发布排期
   * 输入：ids (string[]) - 排期ID数组
   * 输出：Promise<void>
   */
  async batchUnpublish(ids: string[]): Promise<void> {
    await httpClient.post(
      `${this.endpoint}batch_update_status/`,
      { ids, status: 'DRAFT' },
    )
  }

  /**
   * 批量删除排期
   * 输入：ids (string[]) - 排期ID数组
   * 输出：Promise<void>
   */
  async batchDelete(ids: string[]): Promise<void> {
    const promises = ids.map(id => this.delete(id))
    await Promise.all(promises)
  }

  // ========================================
  // ⭐ M5-NEW: 企业端专用查询方法
  // ========================================

  /**
   * 按职场查询排期（企业端）
   * 
   * @param params 查询参数
   * @returns 排期列表（已发布的排期）
   * 
   * @example
   * ```typescript
   * // 查询职场的午餐排期
   * const schedules = await menuScheduleApi.getByWorkplace({
   *   workplace_id: '123',
   *   meal_segment: 'WC0003',
   *   status: 'PUBLISHED'
   * })
   * ```
   */
  async getByWorkplace(params: {
    workplace_id: string
    meal_segment?: string
    consumption_scene?: string
    scheduled_date_single?: string
    page?: number
    page_size?: number
  }): Promise<PaginatedMenuScheduleResponse> {
    return this.list({
      workplace: params.workplace_id, // 后端参数名是 workplace
      meal_segment: params.meal_segment,
      consumption_scene: params.consumption_scene,
      scheduled_date_single: params.scheduled_date_single,
      status: 'PUBLISHED', // 企业端只显示已发布的
      page: params.page,
      page_size: params.page_size
    })
  }

  /**
   * 按日期查询排期（企业端）
   * 
   * @param params 查询参数
   * @returns 排期列表
   * 
   * @example
   * ```typescript
   * // 查询指定日期的排期
   * const schedules = await menuScheduleApi.getByDate({
   *   workplace_id: '123',
   *   scheduled_date: '2025-11-26',
   *   meal_segment: 'WC0003'
   * })
   * ```
   */
  async getByDate(params: {
    workplace_id: string
    scheduled_date: string
    meal_segment?: string
    consumption_scene?: string
  }): Promise<PaginatedMenuScheduleResponse> {
    return this.list({
      workplace: params.workplace_id,
      scheduled_date_single: params.scheduled_date,
      meal_segment: params.meal_segment,
      consumption_scene: params.consumption_scene,
      status: 'PUBLISHED',
      page: 1,
      page_size: 100
    })
  }

  /**
   * 查询月度菜单可用性（企业端）
   * 
   * @param params 查询参数
   * @returns 日期到餐段的映射 { '2025-11-26': ['WC0003', 'WC0004'], ... }
   * 
   * @example
   * ```typescript
   * const availability = await menuScheduleApi.getMonthlyAvailability({
   *   workplace_id: '123',
   *   year: 2025,
   *   month: 11
   * })
   * console.log(availability['2025-11-26']) // ['WC0003', 'WC0004']
   * ```
   */
  async getMonthlyAvailability(params: {
    workplace_id: string
    year: number
    month: number
  }): Promise<Record<string, string[]>> {
    // 生成该月份的日期范围
    const startDate = `${params.year}-${String(params.month).padStart(2, '0')}-01`
    const _endD = new Date(params.year, params.month, 0)
    const endDate = `${_endD.getFullYear()}-${String(_endD.getMonth() + 1).padStart(2, '0')}-${String(_endD.getDate()).padStart(2, '0')}`
    
    // 查询该月份的所有排期
    const response = await this.list({
      workplace: params.workplace_id,
      status: 'PUBLISHED',
      page: 1,
      page_size: 500 // 假设一个月不会超过 500 条排期
    })
    
    // 构建日期到餐段的映射
    const availability: Record<string, Set<string>> = {}
    
    for (const schedule of response.results) {
      for (const dateStr of schedule.scheduled_dates) {
        // 只保留该月份的日期
        if (dateStr >= startDate && dateStr <= endDate) {
          if (!availability[dateStr]) {
            availability[dateStr] = new Set()
          }
          availability[dateStr].add(schedule.meal_segment)
        }
      }
    }
    
    // 将 Set 转换为 Array
    const result: Record<string, string[]> = {}
    for (const [date, segments] of Object.entries(availability)) {
      result[date] = Array.from(segments)
    }
    
    return result
  }

  /**
   * 按日期和餐段查询可用餐厅（企业端）
   * 
   * @param params 查询参数
   * @returns 餐厅列表（去重）
   * 
   * @example
   * ```typescript
   * const restaurants = await menuScheduleApi.getAvailableRestaurants({
   *   workplace_id: '123',
   *   scheduled_date: '2025-11-26',
   *   meal_segment: 'WC0003'
   * })
   * ```
   */
  async getAvailableRestaurants(params: {
    workplace_id: string
    scheduled_date: string
    meal_segment: string
    consumption_scene?: string
  }): Promise<Array<{
    restaurant_id: string
    restaurant_name: string
    is_orderable: boolean
    order_deadline?: string
    menu_detail?: MenuDetail
    schedule_id: string
    supply_capacity?: number | null
    supply_remaining?: number | null
  }>> {
    const response = await this.list({
      workplace: params.workplace_id,
      scheduled_date_single: params.scheduled_date,
      meal_segment: params.meal_segment,
      consumption_scene: params.consumption_scene,
      status: 'PUBLISHED',
      page: 1,
      page_size: 100
    })
    
    const restaurantMap = new Map<string, {
      restaurant_id: string
      restaurant_name: string
      is_orderable: boolean
      order_deadline?: string
      menu_detail?: MenuDetail
      schedule_id: string
      supply_capacity?: number | null
      supply_remaining?: number | null
    }>()
    
    for (const schedule of response.results) {
      if (!restaurantMap.has(schedule.restaurant)) {
        restaurantMap.set(schedule.restaurant, {
          restaurant_id: schedule.restaurant,
          restaurant_name: schedule.restaurant_name,
          is_orderable: schedule.is_orderable || false,
          order_deadline: schedule.order_deadline,
          menu_detail: schedule.menu_detail,
          schedule_id: schedule.id,
          supply_capacity: schedule.supply_capacity ?? null,
          supply_remaining: schedule.supply_remaining ?? null,
        })
      }
    }
    
    return Array.from(restaurantMap.values())
  }
}

export const menuScheduleApi = new MenuScheduleApiService()
